import { useState, useEffect } from 'react';
import { Bell, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export const NotificationPreferences = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch user preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!userProfile?.id) return;

      try {
        const { data, error } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', userProfile.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw error;
        }

        // If no preferences exist, they'll be auto-created by trigger
        // Use defaults in the meantime
        setPreferences(data || {
          user_id: userProfile.id,
          app_mentions: true,
          app_comments: true,
          app_replies: true,
          app_assignments: true,
          app_expiry_warnings: true,
          app_status_changes: true,
          app_invitations: true,
          app_check_ins: true,
          app_team_updates: true,
          email_mentions: true,
          email_comments: false,
          email_replies: false,
          email_assignments: true,
          email_expiry_warnings: true,
          email_status_changes: false,
          email_invitations: true,
          email_check_ins: false,
          email_team_updates: false,
          email_digest_enabled: false,
          email_digest_frequency: 'daily',
          email_digest_time: '09:00:00',
          push_enabled: false
        });
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        toast.error('Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [userProfile?.id]);

  const handleToggle = (field) => {
    setPreferences(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setHasChanges(true);
  };

  const handleSelectChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!userProfile?.id || !preferences) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: userProfile.id,
          ...preferences,
          updated_date: new Date().toISOString()
        });

      if (error) throw error;

      setHasChanges(false);
      toast.success('Notification preferences saved successfully');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!preferences) {
    return null;
  }

  const notificationTypes = [
    { key: 'mentions', label: 'Mentions', description: 'When someone mentions you with @' },
    { key: 'comments', label: 'Comments', description: 'When someone comments on your content' },
    { key: 'replies', label: 'Replies', description: 'When someone replies to your comment' },
    { key: 'assignments', label: 'Assignments', description: 'When you are assigned to a task or training' },
    { key: 'expiry_warnings', label: 'Expiry Warnings', description: 'When certifications are about to expire' },
    { key: 'status_changes', label: 'Status Changes', description: 'When status of tasks or training changes' },
    { key: 'invitations', label: 'Invitations', description: 'When you are invited to projects or teams' },
    { key: 'check_ins', label: 'Check-ins', description: 'Reminders for required check-ins' },
    { key: 'team_updates', label: 'Team Updates', description: 'Updates about your team members' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Choose how you want to be notified about different activities
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Types Table */}
        <div className="space-y-3">
          {notificationTypes.map((type) => (
            <div key={type.key} className="space-y-2">
              <div>
                <div className="text-sm font-medium">{type.label}</div>
                <div className="text-xs text-gray-500">{type.description}</div>
              </div>
              <div className="flex items-center gap-6 pl-4">
                <div className="flex items-center gap-2 min-w-[100px]">
                  <Bell className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">App</span>
                  <Switch
                    checked={preferences[`app_${type.key}`]}
                    onCheckedChange={() => handleToggle(`app_${type.key}`)}
                  />
                </div>
                <div className="flex items-center gap-2 min-w-[100px]">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Email</span>
                  <Switch
                    checked={preferences[`email_${type.key}`]}
                    onCheckedChange={() => handleToggle(`email_${type.key}`)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* Email Digest Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="email_digest" className="text-base font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Email Digest
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                Receive a summary of notifications instead of individual emails
              </p>
            </div>
            <Switch
              id="email_digest"
              checked={preferences.email_digest_enabled}
              onCheckedChange={() => handleToggle('email_digest_enabled')}
            />
          </div>

          {preferences.email_digest_enabled && (
            <div className="pl-6 space-y-3">
              <div>
                <Label htmlFor="digest_frequency" className="text-sm">Frequency</Label>
                <Select
                  value={preferences.email_digest_frequency}
                  onValueChange={(value) => handleSelectChange('email_digest_frequency', value)}
                >
                  <SelectTrigger id="digest_frequency" className="w-full sm:w-[200px] mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="never">Never</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="digest_time" className="text-sm">Time of Day</Label>
                <Select
                  value={preferences.email_digest_time}
                  onValueChange={(value) => handleSelectChange('email_digest_time', value)}
                >
                  <SelectTrigger id="digest_time" className="w-full sm:w-[200px] mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="06:00:00">6:00 AM</SelectItem>
                    <SelectItem value="09:00:00">9:00 AM</SelectItem>
                    <SelectItem value="12:00:00">12:00 PM</SelectItem>
                    <SelectItem value="15:00:00">3:00 PM</SelectItem>
                    <SelectItem value="18:00:00">6:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Push Notifications (Future Feature) */}
        <div className="space-y-4 opacity-50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label className="text-base font-medium">Push Notifications</Label>
              <p className="text-sm text-gray-500 mt-1">
                Mobile push notifications (Coming Soon)
              </p>
            </div>
            <Switch
              checked={false}
              disabled={true}
            />
          </div>
        </div>

        {/* Save Button */}
        {hasChanges && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <div className="h-2 w-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span>You have unsaved changes</span>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
